import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getConnectUser,
  getConnectToken,
  connectAuthPost,
  connectUploadImage,
  clearConnectSession,
} from './connectApi';
import ConnectNav from './ConnectNav';
import NotificationsBell from './NotificationsBell';
import PostComments from './PostComments';
import ComposerImage from './ComposerImage';
import './styles/ConnectFeed.css';

/**
 * The home feed: posts from people the member follows, plus anyone whose
 * profile is public, newest first.
 *
 * Paging is a cursor on (timestamp, id) rather than a page number, because
 * posts arrive while someone is reading and an offset would repeat or skip
 * rows. New arrivals are not spliced in underneath the reader either — they
 * are counted, and a pill offers them, so the page never jumps.
 */

const POLL_MS = 30000;

const shapePost = (p) => ({
  id: p.id,
  userId: p.user_id,
  authorName: p.author_name || 'Member',
  authorPicture: p.author_picture || '',
  viewerFollows: !!p.viewer_follows,
  isOwn: !!p.is_own,
  content: p.content || '',
  image: p.image || null,
  location: p.location || '',
  likes: p.likes ?? 0,
  comments: p.comments ?? 0,
  likedByViewer: !!p.liked_by_viewer,
  timestamp: (p.timestamp || 0) * 1000,
  rawTime: p.timestamp || 0,
});

const timeAgo = (ms) => {
  const seconds = Math.floor((Date.now() - ms) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export default function ConnectFeed() {
  const navigate = useNavigate();
  const [user, setUser] = useState(getConnectUser());
  const [showMobileNav, setShowMobileNav] = useState(false);

  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null); // { time, id }
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [newCount, setNewCount] = useState(0);
  const [openCommentsPostId, setOpenCommentsPostId] = useState(null);

  // Composer
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostLocation, setNewPostLocation] = useState('');
  const [newPostImageFile, setNewPostImageFile] = useState(null);
  const [isPosting, setIsPosting] = useState(false);
  const postImageInputRef = useRef(null);

  // The timestamp of the newest post on screen, used to count arrivals
  const newestRef = useRef(0);

  useEffect(() => {
    if (!getConnectToken()) {
      navigate('/connect');
      return;
    }
    setUser(getConnectUser());
  }, [navigate]);

  const loadFeed = useCallback(async ({ append = false, before = null } = {}) => {
    try {
      const payload = { limit: 20 };
      if (before) {
        payload.before_time = before.time;
        payload.before_id = before.id;
      }
      const result = await connectAuthPost('connect/feed', payload);

      if (!result.success) {
        setError(result.response || 'Could not load the feed.');
        return;
      }

      const incoming = (result.data.posts || []).map(shapePost);
      setPosts(prev => (append ? [...prev, ...incoming] : incoming));
      setHasMore(!!result.data.has_more);
      setCursor(result.data.has_more
        ? { time: result.data.next_time, id: result.data.next_id }
        : null);
      setError('');

      if (!append && incoming.length) {
        newestRef.current = incoming[0].rawTime;
        setNewCount(0);
      }
    } catch (err) {
      setError('Could not reach the server.');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      await loadFeed();
      if (!cancelled) setIsLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [loadFeed]);

  // Count what has arrived since the top of the list, without moving anything
  useEffect(() => {
    if (isLoading) return undefined;

    const check = async () => {
      if (document.hidden || !newestRef.current) return;
      try {
        const result = await connectAuthPost('connect/feed/new-count', {
          since_time: newestRef.current,
        });
        if (result.success) setNewCount(result.data.count || 0);
      } catch (err) {
        // A failed count is not worth surfacing; the next tick will retry
      }
    };

    const id = setInterval(check, POLL_MS);
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isLoading]);

  const handleLoadMore = async () => {
    if (!cursor || isLoadingMore) return;
    setIsLoadingMore(true);
    await loadFeed({ append: true, before: cursor });
    setIsLoadingMore(false);
  };

  const handleShowNew = async () => {
    setIsLoading(true);
    await loadFeed();
    setIsLoading(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() || isPosting) return;
    setIsPosting(true);
    try {
      let imageUrl = '';
      if (newPostImageFile) {
        const uploadResult = await connectUploadImage(newPostImageFile, 'post');
        if (uploadResult.success) imageUrl = uploadResult.data.url;
      }

      const result = await connectAuthPost('connect/posts/create', {
        content: newPostContent.trim(),
        image: imageUrl,
        location: newPostLocation.trim(),
      });

      if (result.success) {
        const own = shapePost({
          ...result.data.post,
          author_name: user?.fullName || 'You',
          author_picture: user?.profilePicture || '',
          is_own: true,
        });
        setPosts(prev => [own, ...prev]);
        newestRef.current = own.rawTime;
        setNewPostContent('');
        setNewPostLocation('');
        setNewPostImageFile(null);
        if (postImageInputRef.current) postImageInputRef.current.value = '';
      }
    } catch (err) {
      setError('Could not post that.');
    } finally {
      setIsPosting(false);
    }
  };

  const handleToggleLike = async (postId) => {
    // Flip first so the tap feels immediate, then reconcile with the server
    setPosts(prev => prev.map(p => p.id === postId
      ? { ...p, likedByViewer: !p.likedByViewer, likes: p.likes + (p.likedByViewer ? -1 : 1) }
      : p));

    try {
      const result = await connectAuthPost('connect/posts/like', { post_id: postId });
      if (result.success) {
        setPosts(prev => prev.map(p => p.id === postId
          ? { ...p, likedByViewer: result.data.liked, likes: result.data.likes }
          : p));
      }
    } catch (err) {
      await loadFeed();
    }
  };

  const handleLogout = async () => {
    try { await connectAuthPost('connect/auth/logout'); } catch (err) { /* clear locally regardless */ }
    clearConnectSession();
    navigate('/connect');
  };

  if (!user) return null;

  return (
    <div className="feed-page">
      <header className="connect-profile-header">
        <Link to="/" className="connect-profile-logo">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="OutCaribbean" />
        </Link>
        <ConnectNav active="feed" />
        <div className="connect-header-actions">
          <NotificationsBell />
          <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
          <button className="connect-mobile-nav-toggle" onClick={() => setShowMobileNav(true)}>
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      {showMobileNav && (
        <>
          <div className="connect-mobile-nav-overlay active" onClick={() => setShowMobileNav(false)}></div>
          <div className="connect-mobile-nav-menu active">
            <div className="connect-mobile-nav-header">
              <h3>Menu</h3>
              <button className="connect-mobile-nav-close" onClick={() => setShowMobileNav(false)}>×</button>
            </div>
            <div className="connect-mobile-nav-links">
              <ConnectNav active="feed" variant="mobile" onNavigate={() => setShowMobileNav(false)} />
            </div>
            <div className="connect-mobile-nav-footer">
              <Link to="/community-guidelines" className="connect-guidelines-link">
                Community Guidelines
              </Link>
              <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
            </div>
          </div>
        </>
      )}

      <main className="feed-main">
        <div className="connect-post-composer">
          <textarea
            value={newPostContent}
            onChange={(e) => setNewPostContent(e.target.value)}
            placeholder="Share your travel experience..."
            rows={3}
            maxLength={2000}
          />
          <ComposerImage
            file={newPostImageFile}
            onClear={() => {
              setNewPostImageFile(null);
              if (postImageInputRef.current) postImageInputRef.current.value = '';
            }}
          />

          <div className="connect-composer-row">
            <input
              type="text"
              value={newPostLocation}
              onChange={(e) => setNewPostLocation(e.target.value)}
              placeholder="📍 Location (optional)"
              className="connect-composer-location"
            />
            <button
              type="button"
              className="connect-composer-photo-btn"
              onClick={() => postImageInputRef.current?.click()}
            >
              {newPostImageFile ? '🖼️ Change photo' : '🖼️ Photo'}
            </button>
            <input
              ref={postImageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => setNewPostImageFile(e.target.files[0] || null)}
            />
            <button
              type="button"
              className="connect-composer-post-btn"
              onClick={handleCreatePost}
              disabled={isPosting || !newPostContent.trim()}
            >
              {isPosting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>

        {newCount > 0 && (
          <button type="button" className="feed-new-pill" onClick={handleShowNew}>
            {newCount === 1 ? '1 new post' : `${newCount} new posts`}
          </button>
        )}

        {error && <p className="feed-error">{error}</p>}

        {isLoading && (
          <div className="cpx-state">
            <h3>Loading your feed</h3>
          </div>
        )}

        {!isLoading && posts.length === 0 && !error && (
          <div className="cpx-state">
            <h3>Nothing here yet</h3>
            <p>
              Follow a few people in Connect, and their posts will show up here
              alongside anyone with a public profile.
            </p>
            <Link to="/connect/discover" className="feed-empty-btn">Find people</Link>
          </div>
        )}

        {posts.map(post => (
          <article key={post.id} className="connect-post-card">
            <div className="connect-post-header">
              <Link to={`/connect/user/${post.userId}`} className="connect-post-avatar">
                {post.authorPicture ? (
                  <img src={post.authorPicture} alt={post.authorName} />
                ) : (
                  <div className="connect-post-avatar-placeholder">
                    {post.authorName.charAt(0).toUpperCase()}
                  </div>
                )}
              </Link>
              <div className="connect-post-meta">
                <Link to={`/connect/user/${post.userId}`} className="connect-post-author">
                  {post.isOwn ? 'You' : post.authorName}
                </Link>
                <span className="connect-post-time">
                  {timeAgo(post.timestamp)}
                  {post.location && ` · ${post.location}`}
                </span>
              </div>
              {!post.isOwn && !post.viewerFollows && (
                <span className="feed-public-tag" title="This member's profile is public">
                  Public
                </span>
              )}
            </div>

            {post.content && <p className="connect-post-content">{post.content}</p>}

            {post.image && (
              <div className="connect-post-image">
                <img src={post.image} alt="" />
              </div>
            )}

            <div className="connect-post-actions">
              <button
                type="button"
                className={`connect-post-action ${post.likedByViewer ? 'liked' : ''}`}
                onClick={() => handleToggleLike(post.id)}
                aria-pressed={post.likedByViewer}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill={post.likedByViewer ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                <span>{post.likes}</span>
              </button>
              <button
                type="button"
                className={`connect-post-action ${openCommentsPostId === post.id ? 'liked' : ''}`}
                onClick={() => setOpenCommentsPostId(
                  openCommentsPostId === post.id ? null : post.id
                )}
                aria-expanded={openCommentsPostId === post.id}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <span>{post.comments}</span>
              </button>
            </div>

            {openCommentsPostId === post.id && (
              <PostComments
                postId={post.id}
                onCountChange={(id, count) => setPosts(prev => prev.map(p =>
                  p.id === id ? { ...p, comments: count } : p
                ))}
              />
            )}
          </article>
        ))}

        {hasMore && !isLoading && (
          <button
            type="button"
            className="feed-more-btn"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading...' : 'Show older posts'}
          </button>
        )}
      </main>
    </div>
  );
}
